import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { adminApi } from '../services/api'
import GestioneDatiSection from '../components/GestioneDatiSection'
import {
  ArrowLeft,
  Building2,
  Users,
  Baby,
  FileText,
  Clock,
  Calendar,
  Mail,
  Phone,
  Loader2,
  Play,
  Pause,
  Trash2,
  RefreshCw,
  AlertTriangle,
  FlaskConical,
  Target,
  Info,
  Shield,
  ShieldOff,
  ChevronDown,
  ChevronUp
} from 'lucide-react'

// Status Badge
const StatusBadge = ({ status }) => {
  const styles = {
    trial: 'bg-blue-100 text-blue-700',
    active: 'bg-green-100 text-green-700',
    suspended: 'bg-yellow-100 text-yellow-700',
    cancelled: 'bg-red-100 text-red-700',
    pending_deletion: 'bg-orange-100 text-orange-700',
    export_only: 'bg-purple-100 text-purple-700',
    deleted: 'bg-gray-100 text-gray-700'
  }

  return (
    <span className={`px-3 py-1 text-sm font-medium rounded-full ${styles[status] || styles.trial}`}>
      {status}
    </span>
  )
}

export default function TenantDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  const [tenant, setTenant] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  // Experimental features
  const [pepEnabled, setPepEnabled] = useState(false)
  const [pepToggling, setPepToggling] = useState(false)
  const [globalPepFlag, setGlobalPepFlag] = useState(null) // null = non caricato

  // Utenti del centro (lazy-loaded per minimizzare esposizione dati)
  const [usersOpen, setUsersOpen] = useState(false)
  const [usersList, setUsersList] = useState(null)
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersError, setUsersError] = useState(null)

  // Modals
  const [showExtendModal, setShowExtendModal] = useState(false)
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [extendDays, setExtendDays] = useState(30)
  const [newPlan, setNewPlan] = useState('professional')
  const [planDuration, setPlanDuration] = useState(30)

  useEffect(() => {
    loadTenant()
  }, [id])

  const loadTenant = async () => {
    try {
      setLoading(true)
      const [tenantRes, expRes] = await Promise.allSettled([
        adminApi.getTenant(id),
        adminApi.getExperimentalStats()
      ])
      if (tenantRes.status === 'fulfilled') {
        const t = tenantRes.value.data.data.tenant
        setTenant(t)
        setSubscription(tenantRes.value.data.data.subscription)
        setStats(tenantRes.value.data.data.stats)
        setPepEnabled(t.experimentalFeatures?.pep?.enabled === true)
      } else {
        throw tenantRes.reason
      }
      if (expRes.status === 'fulfilled') {
        setGlobalPepFlag(expRes.value.data.data.pep?.globalEnabled === true)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleExtendTrial = async () => {
    try {
      setActionLoading(true)
      await adminApi.extendTrial(id, extendDays)
      setShowExtendModal(false)
      loadTenant()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore')
    } finally {
      setActionLoading(false)
    }
  }

  const handleChangePlan = async () => {
    try {
      setActionLoading(true)
      await adminApi.changePlan(id, newPlan, planDuration)
      setShowPlanModal(false)
      loadTenant()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore')
    } finally {
      setActionLoading(false)
    }
  }

  const handleSuspend = async () => {
    if (!confirm('Sei sicuro di voler sospendere questo centro?')) return
    try {
      setActionLoading(true)
      await adminApi.suspendTenant(id)
      loadTenant()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore')
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivate = async () => {
    try {
      setActionLoading(true)
      await adminApi.reactivateTenant(id)
      loadTenant()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore')
    } finally {
      setActionLoading(false)
    }
  }

  const handleTogglePep = async (enabled) => {
    try {
      setPepToggling(true)
      await adminApi.toggleExperimentalFeature(id, 'pep', enabled)
      setPepEnabled(enabled)
    } catch (err) {
      const code = err.response?.data?.code
      if (code === 'GLOBAL_FLAG_DISABLED') {
        alert('Il flag globale PEP è disabilitato. Attivalo prima nelle Impostazioni di sistema.')
      } else if (code === 'PLAN_NOT_ELIGIBLE') {
        alert(err.response?.data?.message || 'Piano non idoneo per il PEP.')
      } else {
        alert(err.response?.data?.message || 'Errore')
      }
    } finally {
      setPepToggling(false)
    }
  }

  const handleToggleUsers = async () => {
    const next = !usersOpen
    setUsersOpen(next)
    if (next && usersList === null && !usersLoading) {
      try {
        setUsersLoading(true)
        setUsersError(null)
        const res = await adminApi.getTenantUsers(id, { limit: 200 })
        setUsersList(res.data.data.users || [])
      } catch (err) {
        setUsersError(err.response?.data?.message || 'Errore nel caricamento degli utenti')
      } finally {
        setUsersLoading(false)
      }
    }
  }

  const handleDelete = async () => {
    try {
      setActionLoading(true)
      await adminApi.deleteTenant(id)
      navigate('/tenants')
    } catch (err) {
      alert(err.response?.data?.message || 'Errore')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (error || !tenant) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error || 'Tenant non trovato'}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Link
        to="/tenants"
        className="inline-flex items-center text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Torna alla lista
      </Link>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center">
              <Building2 className="h-8 w-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
              <p className="text-gray-500">{tenant.organizationId?.name || tenant.slug}</p>
            </div>
          </div>
          <StatusBadge status={tenant.status} />
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats?.users || 0}</p>
            <p className="text-sm text-gray-500">Utenti</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats?.minori || 0}</p>
            <p className="text-sm text-gray-500">Minori</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats?.entries || 0}</p>
            <p className="text-sm text-gray-500">Registrazioni</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900 capitalize">{tenant.currentPlan}</p>
            <p className="text-sm text-gray-500">Piano</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Azioni</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowExtendModal(true)}
            className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center"
          >
            <Clock className="h-4 w-4 mr-2" />
            Estendi Trial
          </button>
          <button
            onClick={() => setShowPlanModal(true)}
            className="px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Cambia Piano
          </button>
          {tenant.status === 'suspended' ? (
            <button
              onClick={handleReactivate}
              disabled={actionLoading}
              className="px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 flex items-center"
            >
              <Play className="h-4 w-4 mr-2" />
              Riattiva
            </button>
          ) : tenant.status !== 'deleted' && (
            <button
              onClick={handleSuspend}
              disabled={actionLoading}
              className="px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 flex items-center"
            >
              <Pause className="h-4 w-4 mr-2" />
              Sospendi
            </button>
          )}
          <button
            onClick={() => setShowDeleteModal(true)}
            className="px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 flex items-center"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Elimina
          </button>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Info */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informazioni</h2>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-gray-500">Tipo Ente</dt>
              <dd className="font-medium text-gray-900 capitalize">{tenant.entityType}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Email</dt>
              <dd className="font-medium text-gray-900">{tenant.contacts?.email}</dd>
            </div>
            {tenant.contacts?.phone && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Telefono</dt>
                <dd className="font-medium text-gray-900">{tenant.contacts.phone}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-gray-500">Registrato il</dt>
              <dd className="font-medium text-gray-900">
                {new Date(tenant.registeredAt).toLocaleDateString('it-IT')}
              </dd>
            </div>
            {tenant.trialEndDate && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Scadenza Trial</dt>
                <dd className="font-medium text-gray-900">
                  {new Date(tenant.trialEndDate).toLocaleDateString('it-IT')}
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-gray-500">Email Verificata</dt>
              <dd className="font-medium text-gray-900">
                {tenant.emailVerified ? '✅ Sì' : '❌ No'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Limits */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Limiti Piano</h2>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-gray-500">Max Minori</dt>
              <dd className="font-medium text-gray-900">
                {tenant.usage?.minoriCount || 0} / {tenant.limits?.maxMinori === 999999 ? '∞' : tenant.limits?.maxMinori}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Max Educatori</dt>
              <dd className="font-medium text-gray-900">
                {tenant.usage?.educatoriCount || 0} / {tenant.limits?.maxEducatori === 999999 ? '∞' : tenant.limits?.maxEducatori}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Max Coordinatori</dt>
              <dd className="font-medium text-gray-900">
                {tenant.usage?.coordinatoriCount || 0} / {tenant.limits?.maxCoordinatori === 999999 ? '∞' : tenant.limits?.maxCoordinatori}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Storico Dati</dt>
              <dd className="font-medium text-gray-900">
                {tenant.limits?.storageMonths === 999 ? 'Illimitato' : `${tenant.limits?.storageMonths} mesi`}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Utenti del centro */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <button
          onClick={handleToggleUsers}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center">
            <Users className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              Coordinatori ed educatori
            </h2>
            <span className="ml-2 text-sm text-gray-500">
              ({stats?.users || 0})
            </span>
          </div>
          {usersOpen ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {usersOpen && (
          <div className="mt-4">
            {usersLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            )}

            {usersError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {usersError}
              </div>
            )}

            {!usersLoading && !usersError && usersList && usersList.length === 0 && (
              <p className="text-sm text-gray-500 py-4 text-center">
                Nessun coordinatore o educatore registrato per questo centro.
              </p>
            )}

            {!usersLoading && !usersError && usersList && usersList.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4 font-medium">Nome</th>
                      <th className="py-2 pr-4 font-medium">Email</th>
                      <th className="py-2 pr-4 font-medium">Ruolo</th>
                      <th className="py-2 pr-4 font-medium">2FA</th>
                      <th className="py-2 pr-4 font-medium">Ultimo accesso</th>
                      <th className="py-2 pr-4 font-medium">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map((u) => {
                      const fullName = [u.nome, u.cognome].filter(Boolean).join(' ') || u.username || '—'
                      const roleStyle = u.role === 'coordinatore'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                      return (
                        <tr key={u._id} className="border-b last:border-b-0">
                          <td className="py-2 pr-4 font-medium text-gray-900">{fullName}</td>
                          <td className="py-2 pr-4 text-gray-700">{u.email || '—'}</td>
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${roleStyle}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            {u.twoFactorEnabled ? (
                              <span className="inline-flex items-center text-green-700" title="2FA attivo">
                                <Shield className="h-4 w-4" />
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-gray-400" title="2FA non attivo">
                                <ShieldOff className="h-4 w-4" />
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-gray-700">
                            {u.lastLogin
                              ? new Date(u.lastLogin).toLocaleDateString('it-IT')
                              : <span className="text-gray-400">mai</span>}
                          </td>
                          <td className="py-2 pr-4">
                            {u.inviteStatus === 'pending' ? (
                              <span className="text-xs text-yellow-700">Invito pendente</span>
                            ) : u.attivo ? (
                              <span className="text-xs text-green-700">Attivo</span>
                            ) : (
                              <span className="text-xs text-gray-500">Disattivato</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Experimental Features */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center mb-4">
          <FlaskConical className="h-5 w-5 text-purple-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Funzionalità Sperimentali</h2>
        </div>

        {/* PEP */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">PEP — Progetto Educativo Personalizzato</p>
                <p className="text-sm text-gray-500">Progetto educativo, timeline, insight e report PDF</p>
              </div>
            </div>

            {(() => {
              const planOk = ['professional', 'enterprise'].includes(tenant.currentPlan)
              const globalOk = globalPepFlag === true
              const disabled = !planOk || !globalOk || pepToggling

              return (
                <button
                  onClick={() => !disabled && handleTogglePep(!pepEnabled)}
                  disabled={disabled}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                    disabled
                      ? 'bg-gray-200 cursor-not-allowed'
                      : pepEnabled
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      pepEnabled && !disabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              )
            })()}
          </div>

          {/* Warning messages */}
          {!['professional', 'enterprise'].includes(tenant.currentPlan) && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-orange-800">Piano non idoneo</p>
                <p className="text-xs text-orange-600">
                  Il piano attuale ({tenant.currentPlan}) non supporta le feature sperimentali.
                  Richiesto: Professional o Enterprise.
                </p>
              </div>
            </div>
          )}

          {globalPepFlag === false && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">Flag globale disattivato</p>
                <p className="text-xs text-blue-600">
                  Attiva prima il flag globale &quot;PEP Sperimentale&quot; nelle{' '}
                  <Link to="/settings" className="underline hover:text-blue-800">Impostazioni di sistema</Link>.
                </p>
              </div>
            </div>
          )}

          {pepEnabled && (
            <div className="mt-3 flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <p className="text-sm text-green-700">
                PEP abilitato per questo centro
                {tenant.experimentalFeatures?.pep?.enabledAt && (
                  <span className="text-xs text-green-500 ml-1">
                    (dal {new Date(tenant.experimentalFeatures.pep.enabledAt).toLocaleDateString('it-IT')})
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Gestione Dati */}
      <GestioneDatiSection tenant={tenant} />

      {/* Extend Trial Modal */}
      {showExtendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Estendi Trial</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Giorni da aggiungere
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value))}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowExtendModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleExtendTrial}
                disabled={actionLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                {actionLoading ? 'Salvataggio...' : 'Estendi'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Cambia Piano</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nuovo Piano
              </label>
              <select
                value={newPlan}
                onChange={(e) => setNewPlan(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Durata (giorni)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={planDuration}
                onChange={(e) => setPlanDuration(parseInt(e.target.value))}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPlanModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleChangePlan}
                disabled={actionLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                {actionLoading ? 'Salvataggio...' : 'Cambia'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-red-600">Elimina Centro</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Sei sicuro di voler eliminare <strong>{tenant.name}</strong>?
              Questa azione è irreversibile e cancellerà tutti i dati associati.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {actionLoading ? 'Eliminazione...' : 'Elimina Definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
